import { Controller } from '@hotwired/stimulus';
import { hiddenFieldIds, type SkipLogicRules } from '../../services/expanded_skip_logic/evaluate';

const ROW = '[data-skip-logic-beneficiary-id]';
const HIDDEN = 'skip-logic-hidden';

// Hidden with an inline style, because stories.sass gives the form's contact rows
// `#story_form div.project_benficiary { display: flex }`, which a class rule can't outrank.
function setHidden(element: HTMLElement, hidden: boolean): void {
  const wasHidden = element.classList.contains(HIDDEN);
  element.classList.toggle(HIDDEN, hidden);
  if (hidden) element.style.display = 'none';
  else if (wasHidden) element.style.removeProperty('display');
}

// Hides each question's row for each contact on the Several or No Contacts story form, from that
// contact's own answers and the survey's skip logic (CP-2184). A hidden row keeps its answer, but
// stops being required so a mandatory question hidden for a contact can't block publishing.
export default class extends Controller<HTMLFormElement> {
  static values = { rules: Object };

  declare readonly rulesValue: SkipLogicRules;

  // Listens on the form for the click as well as the change, because the form's own jQuery code
  // clears answers and puts required back without firing a change this listener can hear.
  private readonly refresh = (event: Event): void => {
    const row = (event.target as Element).closest<HTMLElement>(ROW);
    if (row) this.refreshContact(row.dataset.skipLogicBeneficiaryId as string);
    this.syncRequired();
  };

  connect(): void {
    const contactIds = new Set(this.rows().map((row) => row.dataset.skipLogicBeneficiaryId));
    for (const contactId of contactIds) this.refreshContact(contactId as string);
    this.syncRequired();
    this.element.addEventListener('change', this.refresh);
    this.element.addEventListener('click', this.refresh);
  }

  disconnect(): void {
    this.element.removeEventListener('change', this.refresh);
    this.element.removeEventListener('click', this.refresh);
  }

  private rows(contactId?: string): HTMLElement[] {
    const selector = contactId ? `[data-skip-logic-beneficiary-id="${contactId}"]` : ROW;
    return [...this.element.querySelectorAll<HTMLElement>(selector)];
  }

  private refreshContact(contactId: string): void {
    const rows = this.rows(contactId);
    const answersFor = (fieldId: number) =>
      rows
        .filter((row) => row.dataset.skipLogicFieldId === String(fieldId))
        .flatMap((row) => [...row.querySelectorAll<HTMLInputElement>('input:checked')])
        .map((input) => Number(input.value));
    const hidden = hiddenFieldIds(this.rulesValue.fields, this.rulesValue.rules, answersFor);

    for (const row of rows) {
      const wasHidden = row.classList.contains(HIDDEN);
      setHidden(row, hidden.has(Number(row.dataset.skipLogicFieldId)));
      if (wasHidden && !row.classList.contains(HIDDEN)) this.reapplyContactSearch(row);
    }
    this.syncQuestionBlocks();
  }

  // The form's "Find a contact" search skips hidden rows, so a row it hid before skip logic did
  // is shown on reveal, then the search is re-run if one is typed in.
  private reapplyContactSearch(row: HTMLElement): void {
    const search = row
      .closest('.parent_expanded_story_indicator')
      ?.querySelector<HTMLInputElement>('.search_term_story_indicators');
    if (search?.value) search.dispatchEvent(new Event('keyup', { bubbles: true }));
  }

  private syncQuestionBlocks(): void {
    for (const block of this.element.querySelectorAll<HTMLElement>('[data-skip-logic-question]')) {
      const rows = [...block.querySelectorAll(ROW)];
      setHidden(block, rows.length > 0 && rows.every((row) => row.classList.contains(HIDDEN)));
    }
  }

  private syncRequired(): void {
    for (const row of this.rows()) {
      const fields = row.querySelectorAll<HTMLInputElement>('input, select, textarea');
      for (const field of fields) {
        if (row.classList.contains(HIDDEN)) {
          if (field.required) {
            field.required = false;
            field.dataset.skipLogicRequired = 'true';
          }
        } else if (field.dataset.skipLogicRequired) {
          field.required = true;
          delete field.dataset.skipLogicRequired;
        }
      }
    }
  }
}
